/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';

export interface EventLayoutSlot {
  /** Zero-based column index within the concurrent group. */
  colIndex: number;
  /** Total columns needed to display all concurrent events at this time. */
  totalCols: number;
  /** Left offset as a percentage (0-100) for overlap layout. */
  leftPct: number;
  /** Width as a percentage (0-100) for overlap layout. */
  widthPct: number;
}

/**
 * Stable key used to identify an expanded (possibly recurring) event instance.
 * @param ev - The expanded event instance.
 * @returns A key combining the event id and its occurrence start.
 */
const evKey = (ev: ExpandedEvent): string =>
  `${ev.event_id}:${ev.occurrence_start}`;

/**
 * Returns the event's start time as epoch milliseconds.
 * @param ev - The expanded event instance.
 * @returns Start time in milliseconds since the epoch.
 */
const evStart = (ev: ExpandedEvent): number =>
  new Date(ev.start_at_utc).getTime();

/**
 * Returns the event's end time as epoch milliseconds.
 * @param ev - The expanded event instance.
 * @returns End time in milliseconds since the epoch.
 */
const evEnd = (ev: ExpandedEvent): number =>
  new Date(ev.end_at_utc).getTime();

/**
 * Determines whether two events overlap in time.
 * @param a - The first event.
 * @param b - The second event.
 * @returns True when the two time ranges intersect.
 */
const overlaps = (a: ExpandedEvent, b: ExpandedEvent): boolean =>
  evStart(a) < evEnd(b) && evEnd(a) > evStart(b);

/**
 * Offset per column as a percentage. Each subsequent overlapping event is
 * shifted right by this amount. The remaining space is used for its width,
 * producing an overlapping cascade (like Google Calendar) rather than equal
 * side-by-side splits that become unreadably narrow in week view.
 */
const COL_OFFSET_PCT = 30;

/**
 * Minimum width percentage for any event chip. Even with many overlaps, events
 * will never shrink below this width.
 */
const MIN_WIDTH_PCT = 50;

/**
 * Furthest left offset a chip may take. Past this the chip would need to be
 * narrower than {@link MIN_WIDTH_PCT} to stay inside its day column — and an
 * unbounded `colIndex * COL_OFFSET_PCT` pushed chips clean out of the column
 * (a fifth overlapping event landed at 120%), which made the week body wider
 * than its container and produced a horizontal scrollbar.
 */
const MAX_LEFT_PCT = 100 - MIN_WIDTH_PCT;

/**
 * Offset applied per column so the whole overlap group fits within the column.
 *
 * Keeps the {@link COL_OFFSET_PCT} cascade while the group is small, then
 * compresses the step as the group grows so the last chip lands exactly at
 * {@link MAX_LEFT_PCT} instead of running off the edge.
 *
 * @param totalCols - Number of columns the overlap group spans.
 * @returns The per-column offset in percent.
 */
const offsetStepFor = (totalCols: number): number =>
  totalCols > 1 ? Math.min(COL_OFFSET_PCT, MAX_LEFT_PCT / (totalCols - 1)) : 0;

/**
 * Finds the first existing column with no time overlap against `ev`.
 * @param cols - Columns of already-placed events.
 * @param ev - The event to place.
 * @returns The index of a free column, or -1 when none is available.
 */
const findFreeColumn = (cols: ExpandedEvent[][], ev: ExpandedEvent): number =>
  cols.findIndex((col) => !col.some((existing) => overlaps(existing, ev)));

/**
 * Greedy column assignment (earliest start first): each event is placed in the
 * first column that has no time overlap, otherwise a new column is opened.
 * @param sorted - Events sorted by start time ascending.
 * @returns Map from event key to its assigned column index.
 */
const assignColumns = (sorted: ExpandedEvent[]): Map<string, number> => {
  const cols: ExpandedEvent[][] = [];
  const colOf = new Map<string, number>();
  for (const ev of sorted) {
    const free = findFreeColumn(cols, ev);
    const target = free === -1 ? cols.length : free;
    if (free === -1) cols.push([ev]);
    else cols[free].push(ev);
    colOf.set(evKey(ev), target);
  }
  return colOf;
};

/**
 * Counts distinct columns occupied by `ev` and everything overlapping it.
 * @param ev - The event whose overlap group is measured.
 * @param events - All events in the same day/column.
 * @param colOf - Map from event key to its assigned column index.
 * @returns The number of distinct columns spanned by the overlap group.
 */
const countUsedColumns = (
  ev: ExpandedEvent,
  events: ExpandedEvent[],
  colOf: Map<string, number>
): number => {
  const cols = events
    .filter((o) => o !== ev && overlaps(o, ev))
    .map((o) => colOf.get(evKey(o)))
    .filter((c): c is number => c !== undefined);
  return new Set<number>([colOf.get(evKey(ev)) ?? 0, ...cols]).size;
};

/**
 * Builds the layout slot for a single event given its assigned column.
 * @param ev - The event to build a slot for.
 * @param events - All events in the same day/column.
 * @param colOf - Map from event key to its assigned column index.
 * @returns The computed layout slot (column, offsets, width).
 */
const buildSlot = (
  ev: ExpandedEvent,
  events: ExpandedEvent[],
  colOf: Map<string, number>
): EventLayoutSlot => {
  const colIndex = colOf.get(evKey(ev)) ?? 0;
  const totalCols = countUsedColumns(ev, events, colOf);
  const leftPct = Math.min(colIndex * offsetStepFor(totalCols), MAX_LEFT_PCT);
  return {
    colIndex,
    totalCols,
    leftPct,
    widthPct: Math.max(MIN_WIDTH_PCT, 100 - leftPct),
  };
};

/**
 * Computes overlapping column slots for a set of events that may overlap in
 * time. Uses a greedy placement strategy (earliest start first) to assign each
 * event a column index. Layout uses an overlap-cascade approach: each event
 * occupies most of the column width, offset to the right for its column index.
 *
 * @param events Events for a single day/column.
 * @returns Map from event key to its layout slot.
 */
export const computeEventLayouts = (
  events: ExpandedEvent[]
): Map<string, EventLayoutSlot> => {
  if (events.length === 0) return new Map();
  const sorted = [...events].sort((a, b) => evStart(a) - evStart(b));
  const colOf = assignColumns(sorted);
  const result = new Map<string, EventLayoutSlot>();
  for (const ev of events) {
    result.set(evKey(ev), buildSlot(ev, events, colOf));
  }
  return result;
};
